// Shared portfolio formatting + computation helpers

export function fmt(v) {
  if (!v && v !== 0) return '₹0';
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (a >= 1e7) return `${sign}₹${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${sign}₹${(a / 1e5).toFixed(1)}L`;
  return `${sign}₹${Math.round(a).toLocaleString('en-IN')}`;
}

export function fmtUSD(v, usdInr) {
  const usd = v / (usdInr || 84);
  if (Math.abs(usd) >= 1e6) return `$${(usd / 1e6).toFixed(2)}M`;
  if (Math.abs(usd) >= 1e3) return `$${(usd / 1e3).toFixed(1)}K`;
  return `$${Math.round(usd).toLocaleString()}`;
}

export function addMonths(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  // month arithmetic (avoids day-of-month overflow skipping 30-day months)
  const total = d.getUTCFullYear() * 12 + d.getUTCMonth() + n;
  const y = Math.floor(total / 12), m = ((total % 12) + 12) % 12;
  const last = new Date(Date.UTC(y, m + 1, 0)); // last day of that month
  return last.toISOString().slice(0, 10);
}

export const shortLabel = iso =>
  new Date(iso + 'T00:00:00Z').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });

export const CLASS_COLOR = { DEBT: '#60a5fa', EQUITY: '#34d399', CASH: '#fbbf24' };

// age in whole years at a given iso date
export function ageAt(dob, iso) {
  if (!dob) return null;
  const b = new Date(dob + 'T00:00:00Z'), d = new Date(iso + 'T00:00:00Z');
  let a = d.getUTCFullYear() - b.getUTCFullYear();
  if (d.getUTCMonth() < b.getUTCMonth() || (d.getUTCMonth() === b.getUTCMonth() && d.getUTCDate() < b.getUTCDate())) a--;
  return a;
}
// calendar year in which primary turns `age`
export function yearAtAge(dob, age) {
  if (!dob) return null;
  return Number(dob.slice(0, 4)) + age;
}

// Age-based milestones for the primary holder, anchored to their birth month.
export const MILESTONE_AGES = [40, 50, 60];

export function milestonesFor(dob) {
  if (!dob) return [];
  const bMonth = Number(dob.slice(5, 7)) - 1; // 0-indexed birth month
  const palette = ['#fb923c', '#f97316', '#ea580c'];
  return MILESTONE_AGES.map((age, i) => ({
    label: `Age ${age}`,
    age,
    year: yearAtAge(dob, age),
    month: bMonth,
    color: palette[i % palette.length],
  }));
}

// Match a forecast date (month-end iso) to a milestone — anchored to DEC of the
// milestone year, so Planning and Actuals line up on the same year-end row.
export function milestoneAt(date, milestones = []) {
  const d = new Date(date + 'T00:00:00Z');
  return milestones.find(m => d.getUTCFullYear() === m.year && d.getUTCMonth() === 11) || null;
}

// matrix: { entity: { asset: value } }, cagrs: { asset: decimal }
export function computeDerived(matrix, meta, entities, cagrs) {
  if (!matrix || !meta?.length) return null;
  const assetTotals = {};
  meta.forEach(m => { assetTotals[m.asset] = 0; });
  for (const e of entities) for (const m of meta) assetTotals[m.asset] += (matrix[e]?.[m.asset] || 0);
  const networth = Object.values(assetTotals).reduce((s, v) => s + v, 0);
  let weighted = 0;
  const classSum = { DEBT: 0, EQUITY: 0, CASH: 0 };
  for (const m of meta) {
    const c = cagrs ? (cagrs[m.asset] ?? m.cagr) : m.cagr;
    weighted += assetTotals[m.asset] * c;
    classSum[m.class] = (classSum[m.class] || 0) + assetTotals[m.asset];
  }
  const blendedCagr = networth ? weighted / networth : 0;
  const classPct = {};
  for (const k in classSum) classPct[k] = networth ? (classSum[k] / networth) * 100 : 0;
  const entityTotals = {};
  for (const e of entities) entityTotals[e] = meta.reduce((s, m) => s + (matrix[e]?.[m.asset] || 0), 0);
  return { assetTotals, networth, blendedCagr, classPct, entityTotals };
}

// Effective monthly investment for a date given a step-schedule of overrides.
// schedule: { isoDate: amount } — applies from that month forward until next override.
export function monthlyFor(date, base, schedule = {}) {
  const dates = Object.keys(schedule).filter(d => d <= date).sort();
  return dates.length ? schedule[dates[dates.length - 1]] : base;
}

// Forecast with per-month investment schedule + withdrawal/deposit + opening overrides
// params: { startDate, opening, monthlyBase, monthlySchedule, annualCagr, months, overrides, flows }
//   overrides: { isoDate: openingValue } — hard-set opening for that month
//   flows: { isoDate: amount } — +deposit / -withdrawal applied to that month
//   monthlySchedule: { isoDate: amount } — step-change to monthly investment from that month on
export function buildForecast({ startDate, opening, monthlyBase, monthlySchedule = {}, annualCagr, months, overrides = {}, flows = {} }) {
  const r = annualCagr / 12;
  const rows = [];
  let bal = opening;
  for (let i = 0; i < months; i++) {
    const date = addMonths(startDate, i);
    if (overrides[date] !== undefined) bal = overrides[date];
    const flow = flows[date] || 0;
    const monthly = monthlyFor(date, monthlyBase, monthlySchedule);
    const interest = (bal + monthly) * r;
    const closing = bal + monthly + interest + flow;
    rows.push({ date, label: shortLabel(date), opening: bal, monthly, interest, flow, closing });
    bal = closing;
  }
  return rows;
}
